import React, { useEffect, useMemo } from 'react';
import LearningZoneTopBar from './LearningZoneTopBar';
import LearningSidebar from './LearningSidebar';
import { buildLearningZoneTree } from './courseSubcategoryRows';
import { withoutHiddenRows } from './hiddenLearningZoneRows';
import useLearningZoneCourses from './useLearningZoneCourses';
import { SIDEBAR_TREE } from '../../../data/learningZoneMockData';

const FONT_LINK_ID = 'learning-zone-font';
const FONT_FAMILY = "'Baloo 2', 'Comic Neue', sans-serif";

function useLearningZoneFont() {
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(link);
  }, []);
}

/**
 * The Learning Zone sidebar's rows (`tree`) and the course content each row opens (`views`).
 * Task types of the published courses (Create Course > Task Type) become extra
 * rows under English / Computer Apps / Stories / Fun with Art, e.g. English > Revision,
 * and so do the subcategories admins added (Courses sidebar > Add Subcategory). Rows
 * without a screen (English > Scenes > Beginners) show the courses placed in them.
 * The categories admins added (Courses > Add Category) are groups of their own.
 * The built-in categories and rows admins deleted (Courses page) are left out.
 */
export function useLearningZoneSidebar() {
  const { categories: courseCategories, subcategories, builtInRows, categoryGroups, hiddenRows, hiddenCategories } =
    useLearningZoneCourses();
  return useMemo(() => {
    const built = buildLearningZoneTree(SIDEBAR_TREE, courseCategories, subcategories, builtInRows, categoryGroups);
    return { ...built, tree: withoutHiddenRows(built.tree, { hiddenRows, hiddenCategories }) };
  }, [courseCategories, subcategories, builtInRows, categoryGroups, hiddenRows, hiddenCategories]);
}

/**
 * The Learning Zone frame - its own top bar, the sidebar and the main area (`children`) -
 * shared by the Learning Zone page and the student's Shop pages (`shopActive`).
 *
 * Standalone (not wrapped in StudentLayout) so it can use its own compact
 * header instead of the site-wide TitleBar/Toolbar. The font-family is set
 * via the --wtf-font-family custom property (the same variable App.css
 * already cascades font choices through) scoped to this frame's own subtree,
 * so it never touches other pages' fonts.
 *
 * Layout: at lg+ the frame is a fixed-height shell - the top bar stays put and
 * the sidebar and main content are separate scroll regions, so a long
 * sidebar tree and a tall main panel no longer scroll together. Below lg it
 * falls back to the normal stacked page scroll.
 */
export default function LearningZoneShell({ sidebarTree, activeView, onSelectView, shopActive = false, children }) {
  useLearningZoneFont();

  return (
    <div
      className="min-h-screen lg:h-screen lg:overflow-hidden flex flex-col bg-gray-100 py-8 px-6"
      style={{ '--wtf-font-family': FONT_FAMILY }}
    >
      <LearningZoneTopBar shopActive={shopActive} />

      <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-stretch lg:flex-1 lg:min-h-0">
        <LearningSidebar items={sidebarTree} activeView={activeView} onSelectView={onSelectView} />
        {children}
      </div>
    </div>
  );
}
